/**
 * Lønnsprognose til PowerOffice (#183 phase 3): the schedule's wage cost per month (wages with holiday
 * pay, pension and employer contribution at the company's rates) sent to PowerOffice Go as Timefly's own
 * budget for the year, on the store's department. The administrator sees the months first and picks who
 * is responsible for the budget in Go (Go requires one). Sending again replaces Timefly's earlier
 * prognosis only; the company's own budgets are never touched.
 */
import { errorText, get, post } from '@/api/client';
import { T } from '@/i18n';
import { Button, toast } from '@/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

interface Plan {
  year: number;
  name: string;
  account: number;
  department: { id: number; name: string } | null;
  months: { month: number; hours: number; cost: number; unpriced: number }[];
  total: number;
  managers: { id: number; number: string; name: string }[];
  manager_suggestion: number | null;
  sent_before: boolean;
}
const kr = (v: number) => `${Math.round(v).toLocaleString('nb-NO')} kr`;
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'mai', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'des'];

export function PowerOfficeWagePlan({ year }: { year: number }) {
  const [open, setOpen] = useState(false);
  const [manager, setManager] = useState<number | null>(null);
  const q = useQuery({
    queryKey: ['poweroffice-wage-plan', year],
    queryFn: () => get<Plan>(`/admin/poweroffice/budget-plan?year=${year}`),
    enabled: open,
    staleTime: 0,
  });
  const send = useMutation({
    mutationFn: () => post<Plan>('/admin/poweroffice/budget-plan', { year, manager_id: chosen }),
    onSuccess: () => {
      q.refetch();
      toast(T('Lønnsprognosen for {year} er sendt til PowerOffice.', { year }));
    },
    onError: (e) => toast(errorText(e)),
  });
  const p = q.data;
  const chosen = manager ?? p?.manager_suggestion ?? null;
  if (!open)
    return (
      <span>
        <Button small onClick={() => setOpen(true)}>
          {T('Send lønnsprognose til PowerOffice')}
        </Button>
      </span>
    );
  return (
    <section data-testid="po-wage-plan" style={{ display: 'grid', gap: 8 }}>
      <b style={{ fontSize: 14 }}>{T('Lønnsprognose {year} fra vaktplanen', { year })}</b>
      {q.isPending ? (
        <output style={{ color: 'var(--ink3)' }}>{T('Regner ut …')}</output>
      ) : q.isError || !p ? (
        <span>{errorText(q.error)}</span>
      ) : (
        <>
          <span style={{ fontSize: 14 }} data-testid="po-wage-plan-months">
            {p.months
              .filter((m) => m.cost > 0)
              .map((m) => `${T(MONTHS[m.month - 1] ?? '')} ${kr(m.cost)}`)
              .join(', ') || T('Ingen vakter med lønn i {year}.', { year })}
          </span>
          <span style={{ fontSize: 14 }}>
            {T('Sum {total} på konto {account}{dept}, med feriepenger, pensjon og arbeidsgiveravgift.', {
              total: kr(p.total),
              account: p.account,
              dept: p.department ? ` (${p.department.name})` : '',
            })}
          </span>
          {p.months.some((m) => m.unpriced > 0) && (
            <span style={{ fontSize: 13, color: 'var(--ink3)' }}>
              {T('Noen vakter har ingen lønn på personen og er regnet som 0 kr.')}
            </span>
          )}
          <label style={{ display: 'grid', gap: 4, fontSize: 14, maxWidth: 360 }}>
            {T('Ansvarlig for budsjettet i PowerOffice')}
            <select
              className="inp"
              value={chosen ?? ''}
              onChange={(e) => setManager(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">{T('Velg en ansatt')}</option>
              {p.managers.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.number})
                </option>
              ))}
            </select>
          </label>
          <span style={{ fontSize: 13, color: 'var(--ink3)' }}>
            {p.sent_before
              ? T('Erstatter prognosen Timefly sendte før. Bedriftens egne budsjetter endres ikke.')
              : T('Lagres som et eget budsjett, «{name}». Bedriftens egne budsjetter endres ikke.', {
                  name: p.name,
                })}
          </span>
          <span>
            <Button
              tone="pri"
              onClick={() => send.mutate()}
              disabled={send.isPending || !chosen || !(p.total > 0)}
            >
              {send.isPending ? T('Sender…') : T('Send til PowerOffice')}
            </Button>
          </span>
        </>
      )}
    </section>
  );
}
